import { AlertTriangle, CalendarRange, GripVertical, MapPin } from "lucide-react";
import { useMemo, useState } from "react";

import { formatDateTime } from "../../lib/dateTime";
import { SearchableSelect } from "../common/SearchableSelect";

const trailerLocationLabel = (row) => (row.trailer_tracked === false ? "Power only (not tracked)" : row.trailer_last_location || "Unknown");
const isRowPlanned = (row) => Boolean(row.driver_id || row.truck_id || row.trailer_id);
const SELECT_TRIGGER_CLASS = "h-7 rounded-lg border-zinc-200 px-2 py-0 text-xs";

const getWarnings = (row, assignmentCountsByDay) => {
  const warnings = [];
  if (row.trailer_status === "Loaded") {
    warnings.push("Loaded trailer");
  }
  if (row.pickup_city && row.trailer_last_location && !row.trailer_last_location.toLowerCase().includes(row.pickup_city.toLowerCase())) {
    warnings.push("Wrong last location");
  }
  const dayCounts = assignmentCountsByDay[row.plan_date] || { drivers: {}, trucks: {}, trailers: {} };
  if (
    (row.driver_id && (dayCounts.drivers[row.driver_id] || 0) > 1)
    || (row.truck_id && (dayCounts.trucks[row.truck_id] || 0) > 1)
    || (row.trailer_id && (dayCounts.trailers[row.trailer_id] || 0) > 1)
  ) {
    warnings.push("Double booked");
  }
  return warnings;
};

const WarningChips = ({ row, warnings, showReady = false }) => (
  <div className="flex flex-wrap gap-1">
    {warnings.map((warning) => (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-800" key={`${row.trip_id}-${warning}`}>
        <AlertTriangle className="h-2.5 w-2.5" /> {warning}
      </span>
    ))}
    {showReady && !warnings.length ? <span className="inline-flex rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-800">Ready</span> : null}
  </div>
);

const CardHeadline = ({ row }) => (
  <p className="min-w-0 truncate text-xs text-zinc-600">
    <span className="text-sm font-semibold text-zinc-950">{row.trip_number}</span>
    <span className="mx-1 text-zinc-300">·</span>
    Load {row.load_number}
    <span className="mx-1 text-zinc-300">·</span>
    <span title={row.customer_name}>{row.customer_name}</span>
  </p>
);

const CardRoute = ({ row }) => (
  <p className="mt-1 flex min-w-0 items-baseline gap-2 text-xs">
    <span className="truncate font-medium text-zinc-800" title={`${row.pickup_city} → ${row.delivery_city}`}>{row.pickup_city} → {row.delivery_city}</span>
    <span className="shrink-0 text-zinc-500">{formatDateTime(row.planned_pickup_at)}</span>
  </p>
);

export const VisualPlanningBoard = ({ assignmentSavingTripId, days, driverOptions, onInvalidDrop, onSavePlan, trailerOptions, truckOptions, viewMode, visiblePlanDate, onViewModeChange, onVisiblePlanDateChange }) => {
  const [draggingRow, setDraggingRow] = useState(null);
  const [queuedPlanDates, setQueuedPlanDates] = useState({});
  const [drafts, setDrafts] = useState({});

  const assignmentCountsByDay = useMemo(() => days.reduce((accumulator, day) => {
    accumulator[day.plan_date] = day.rows.reduce((dayAccumulator, row) => {
      if (row.driver_id) {
        dayAccumulator.drivers[row.driver_id] = (dayAccumulator.drivers[row.driver_id] || 0) + 1;
      }
      if (row.truck_id) {
        dayAccumulator.trucks[row.truck_id] = (dayAccumulator.trucks[row.truck_id] || 0) + 1;
      }
      if (row.trailer_id) {
        dayAccumulator.trailers[row.trailer_id] = (dayAccumulator.trailers[row.trailer_id] || 0) + 1;
      }
      return dayAccumulator;
    }, { drivers: {}, trucks: {}, trailers: {} });
    return accumulator;
  }, {}), [days]);

  const visibleDays = useMemo(() => viewMode === "single" ? days.filter((day) => day.plan_date === visiblePlanDate) : days, [days, viewMode, visiblePlanDate]);

  const rowsWithDay = useMemo(() => days.flatMap((day) => day.rows.map((row) => ({ ...row, plan_date: day.plan_date, day_label: day.label }))), [days]);

  const unplannedRows = useMemo(() => rowsWithDay.filter((row) => !isRowPlanned(row) && !queuedPlanDates[row.trip_id]), [queuedPlanDates, rowsWithDay]);

  const getRowDraft = (row) => drafts[row.trip_id] || { driver_id: row.driver_id || "", truck_id: row.truck_id || "", trailer_id: row.trailer_id || "" };

  const rowsForDay = (day) => rowsWithDay.filter((row) => row.plan_date === day.plan_date && (isRowPlanned(row) || queuedPlanDates[row.trip_id] === day.plan_date));

  const updateDraft = (tripId, field, value) => {
    setDrafts((current) => ({ ...current, [tripId]: { ...(current[tripId] || {}), [field]: value } }));
  };

  const handleDrop = (day, row) => {
    if (row.plan_date !== day.plan_date) {
      onInvalidDrop?.();
      return;
    }
    setQueuedPlanDates((current) => ({ ...current, [row.trip_id]: day.plan_date }));
    setDrafts((current) => ({
      ...current,
      [row.trip_id]: current[row.trip_id] || { driver_id: row.driver_id || "", truck_id: row.truck_id || "", trailer_id: row.trailer_id || "" },
    }));
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-zinc-200 bg-[linear-gradient(180deg,#fffdf7,#f5f7fb)] text-zinc-900 shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="planning-visual-board">
      <div className="border-b border-zinc-200 px-4 py-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700" data-testid="planning-visual-board-eyebrow">Drag planner</p>
            <h2 className="mt-1 text-lg font-semibold text-zinc-950" data-testid="planning-visual-board-title">Unplanned on the left · day columns on the right</h2>
            <p className="mt-0.5 text-xs text-zinc-600" data-testid="planning-visual-board-description">Drop a card into its day, then pick Driver / Truck / Trailer inline and Save.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-zinc-200 bg-white p-0.5 shadow-sm" data-testid="planning-visual-view-toggle">
              <button className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${viewMode === "all" ? "bg-sky-600 text-white" : "text-zinc-600"}`} data-testid="planning-visual-view-all-button" onClick={() => onViewModeChange("all")} type="button">All 3 days</button>
              <button className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${viewMode === "single" ? "bg-sky-600 text-white" : "text-zinc-600"}`} data-testid="planning-visual-view-single-button" onClick={() => onViewModeChange("single")} type="button">One day</button>
            </div>
            {viewMode === "single" ? (
              <select className="rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-900 shadow-sm" data-testid="planning-visual-day-select" onChange={(event) => onVisiblePlanDateChange(event.target.value)} value={visiblePlanDate}>
                {days.map((day) => <option key={day.plan_date} value={day.plan_date}>{day.label} • {day.plan_date}</option>)}
              </select>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-0 xl:grid-cols-[300px_minmax(0,1fr)]">
        <div className="border-b border-zinc-200 bg-white/80 xl:border-b-0 xl:border-r" data-testid="planning-visual-unplanned-column">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-200 px-3 py-2.5">
            <div className="flex items-center gap-2 text-zinc-900">
              <CalendarRange className="h-4 w-4 text-sky-700" />
              <h3 className="text-sm font-semibold" data-testid="planning-visual-unplanned-title">Unplanned</h3>
            </div>
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700" data-testid="planning-visual-unplanned-count">{unplannedRows.length}</span>
          </div>
          <div className="max-h-[760px] space-y-2 overflow-y-auto px-3 py-3" data-testid="planning-visual-unplanned-list">
            {unplannedRows.length ? unplannedRows.map((row) => {
              const warnings = getWarnings(row, assignmentCountsByDay);
              return (
                <div
                  className="cursor-grab rounded-xl border border-zinc-200 bg-white px-2.5 py-2 shadow-sm transition hover:border-sky-300 active:cursor-grabbing"
                  data-testid={`planning-visual-unplanned-card-${row.trip_id}-${row.plan_date}`}
                  draggable
                  key={`${row.trip_id}-${row.plan_date}`}
                  onDragStart={() => setDraggingRow(row)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <CardHeadline row={row} />
                      <CardRoute row={row} />
                    </div>
                    <GripVertical className="h-4 w-4 shrink-0 text-zinc-400" />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <span className="inline-flex rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-700" data-testid={`planning-visual-unplanned-day-${row.trip_id}-${row.plan_date}`}>{row.day_label} • {row.plan_date}</span>
                    <WarningChips row={row} showReady warnings={warnings} />
                  </div>
                </div>
              );
            }) : (
              <div className="rounded-xl border border-dashed border-zinc-300 bg-white/70 px-4 py-8 text-center text-xs text-zinc-500" data-testid="planning-visual-unplanned-empty">
                No unplanned loads or trips in this view.
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto" data-testid="planning-visual-day-columns-scroll">
          <div className="grid min-w-[880px] gap-3 bg-[linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:28px_28px] px-3 py-3" style={{ gridTemplateColumns: `repeat(${visibleDays.length}, minmax(0, 1fr))` }}>
            {visibleDays.map((day) => {
              const dayRows = rowsForDay(day);
              return (
                <div
                  className="rounded-2xl border border-zinc-200 bg-white/80 shadow-sm"
                  data-testid={`planning-visual-day-column-${day.plan_date}`}
                  key={day.plan_date}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (draggingRow) {
                      handleDrop(day, draggingRow);
                      setDraggingRow(null);
                    }
                  }}
                >
                  <div className="flex items-baseline justify-between gap-2 border-b border-zinc-200 px-3 py-2">
                    <div className="flex items-baseline gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">{day.label}</p>
                      <p className="text-sm font-semibold text-zinc-950">{day.plan_date}</p>
                    </div>
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700" data-testid={`planning-visual-day-count-${day.plan_date}`}>{dayRows.length}</span>
                  </div>
                  <div className="space-y-2 px-3 py-3" data-testid={`planning-visual-day-list-${day.plan_date}`}>
                    {dayRows.length ? dayRows.map((row) => {
                      const draft = getRowDraft(row);
                      const warnings = getWarnings(row, assignmentCountsByDay);
                      const saving = assignmentSavingTripId === row.trip_id;
                      return (
                        <div className="rounded-xl border border-zinc-200 bg-white px-2.5 py-2 shadow-sm" data-testid={`planning-visual-day-card-${row.trip_id}-${day.plan_date}`} key={`${row.trip_id}-${day.plan_date}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <CardHeadline row={row} />
                              <CardRoute row={row} />
                            </div>
                            {warnings.length ? <WarningChips row={row} warnings={warnings} /> : null}
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-1.5">
                            <SearchableSelect dataTestId={`planning-visual-driver-${row.trip_id}-${day.plan_date}`} onChange={(value) => updateDraft(row.trip_id, "driver_id", value)} options={driverOptions} placeholder="Driver" searchPlaceholder="Search drivers..." triggerClassName={SELECT_TRIGGER_CLASS} value={draft.driver_id} />
                            <SearchableSelect dataTestId={`planning-visual-truck-${row.trip_id}-${day.plan_date}`} onChange={(value) => updateDraft(row.trip_id, "truck_id", value)} options={truckOptions} placeholder="Truck" searchPlaceholder="Search trucks..." triggerClassName={SELECT_TRIGGER_CLASS} value={draft.truck_id} />
                            <SearchableSelect dataTestId={`planning-visual-trailer-${row.trip_id}-${day.plan_date}`} onChange={(value) => updateDraft(row.trip_id, "trailer_id", value)} options={trailerOptions} placeholder="Trailer" searchPlaceholder="Search trailers..." triggerClassName={SELECT_TRIGGER_CLASS} value={draft.trailer_id} />
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-2">
                            <p className="flex min-w-0 items-center gap-1 text-[11px] text-zinc-500" data-testid={`planning-visual-location-${row.trip_id}-${day.plan_date}`} title={`Truck last: ${row.truck_last_location || "Unknown"} • Trailer last: ${trailerLocationLabel(row)}`}>
                              <MapPin className="h-3 w-3 shrink-0 text-zinc-400" />
                              <span className="truncate">Truck: {row.truck_last_location || "Unknown"} • Trailer: {trailerLocationLabel(row)}</span>
                            </p>
                            <button className="shrink-0 rounded-lg bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-sky-500 disabled:opacity-70" data-testid={`planning-visual-save-${row.trip_id}-${day.plan_date}`} disabled={saving} onClick={() => onSavePlan({ plan_date: day.plan_date, trip_id: row.trip_id, ...draft })} type="button">{saving ? "Saving…" : "Save"}</button>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white/65 px-4 text-center text-xs text-zinc-500" data-testid={`planning-visual-day-empty-${day.plan_date}`}>
                        Drop matching unplanned cards here.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
